import { constants as datetimeConstants } from '@eventbrite/datetime-fns';
import { Avatar } from '@eventbrite/marmalade';
import { parse } from 'date-fns';
import React from 'react';
import { Host, Slot } from './types';

import styles from './AgendaSlot.module.scss';

const SlotHosts = ({ hosts }: { hosts: Host[] }) => {
    const hostsWithImages = React.useMemo(
        () => hosts.filter((host) => host.image?.url),
        [hosts],
    ).map((host, index) => (
        <Avatar
            size="small"
            key={host.name}
            imageSrc={host.image?.url}
            imageAlt={host.name}
            className={styles.agendaSlot__hostsImage}
            style={{ zIndex: hosts.length - index }}
        />
    ));

    if (hosts.length === 0) return null;

    return (
        <div className={styles.agendaSlot__hosts}>
            <div className={styles.agendaSlot__hostsImages}>
                {hostsWithImages}
            </div>
            <div className={styles.agendaSlot__hostsNames}>
                {hosts.map((host) => host.name).join(', ')}
            </div>
        </div>
    );
};

/**
 * Converts a 24-hour time string (e.g., "14:30") to locale-specific format.
 *
 * EB-314288: Agenda times are stored as 24-hour strings in the backend.
 * This function converts them to the user's locale format (12-hour for US/Canada/HK, 24-hour for others).
 */
const convertTimeStringToLocaleString = (timeString: string): string => {
    const date = parse(
        timeString,
        datetimeConstants.TIME_FORMAT_24HS,
        new Date(),
    );
    // Pass empty array to use the default locale (works in both browser and SSR)
    return date.toLocaleString([], {
        hour: 'numeric',
        minute: 'numeric',
    });
};

const SlotTime = ({
    startTime,
    endTime,
}: {
    startTime?: string;
    endTime?: string;
}) => {
    if (!startTime) return null;

    const localeStartTime = convertTimeStringToLocaleString(startTime);

    if (!endTime) {
        return (
            <span className={styles.agendaSlot__time}>{localeStartTime}</span>
        );
    }

    const localeEndTime = convertTimeStringToLocaleString(endTime);

    return (
        <span className={styles.agendaSlot__time}>
            {localeStartTime} - {localeEndTime}
        </span>
    );
};

const SlotTitle = ({ title }: { title?: string }) => {
    if (!title) return null;
    return <p className={styles.agendaSlot__title}>{title}</p>;
};

const SlotDescription = ({ description }: { description?: string }) => {
    if (!description) return null;
    return <p className={styles.agendaSlot__description}>{description}</p>;
};

export const AgendaSlot = ({ slot }: { slot: Partial<Slot> }) => {
    return (
        <div className={styles.agendaSlot__container}>
            <SlotTime startTime={slot.startTime} endTime={slot.endTime} />
            <SlotTitle title={slot.title} />
            <SlotHosts hosts={slot.hosts || []} />
            <SlotDescription description={slot.description} />
        </div>
    );
};
